import { lazy, Suspense, useEffect, useRef, type RefObject } from 'react';
import { useSearchParams, type RouteObject } from 'react-router-dom';
import { AppHeader } from '@/components/layout/AppHeader';
import { ROUTES } from '@/app/router/routes';
import { ONBOARDING_ENABLED } from '../gating/isOnboardingEnabled';
import { LoadingSkeleton } from '../shell/LoadingSkeleton';
import { requisitionOf } from './jobLink';
import styles from './onboardingRoute.module.css';

/**
 * The page and everything behind it load on first visit, in their own chunk. Behind the build-time gate, so a
 * production build has no import of it at all.
 */
const loadPage = ONBOARDING_ENABLED ? () => import('../OnboardingPage') : null;

const OnboardingPage = loadPage ? lazy(() => loadPage().then((module) => ({ default: module.OnboardingPage }))) : null;

// Opened at /onboarding (a link or a refresh), the chunk loads with the app, beside the tenant and sign-in calls, not
// after them when the route first renders. A failure here is the route's to report, when it asks again.
if (loadPage && typeof window !== 'undefined' && window.location.pathname === ROUTES.ONBOARDING) loadPage().catch(() => undefined);

/**
 * Publishes the header's height as `--onboarding-header-height` on the route, so the sticky nav sits just under it
 * whatever size the tenant's logo gives the header. It follows the header when its size changes.
 */
function useHeaderHeight(route: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const node = route.current;
    const header = node?.querySelector(':scope > header');
    if (!node || !header) return;
    const publish = () => node.style.setProperty('--onboarding-header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, [route]);
}

/**
 * The app's own tenant header (logo, tenant links, saved jobs, language, account) above the page, as on every other
 * page (decision 82). It is the one old reusable component the feature shows, and only this file imports it: the
 * page and its shell stay free of the app, so they render in tests without it. The body is behind the build-time
 * gate too, so a production build keeps none of it (checked with the prod build, as for the page).
 */
const RouteWithHeader = ONBOARDING_ENABLED
  ? function RouteWithHeader() {
      const route = useRef<HTMLDivElement>(null);
      useHeaderHeight(route);
      const [search] = useSearchParams();
      const requisition = requisitionOf(search);
      return (
        <div ref={route} className={styles.route}>
          <AppHeader />
          <Suspense fallback={<LoadingSkeleton />}>{OnboardingPage ? <OnboardingPage requisition={requisition} /> : null}</Suspense>
        </div>
      );
    }
  : null;

export function OnboardingRoute() {
  return RouteWithHeader ? <RouteWithHeader /> : null;
}

/**
 * The feature's child routes for the router's ProtectedRoute group. None when the feature is off, so
 * /onboarding falls through to the catch-all like any unknown path.
 */
export function onboardingRoutes(enabled = ONBOARDING_ENABLED): RouteObject[] {
  return enabled ? [{ path: ROUTES.ONBOARDING, element: <OnboardingRoute /> }] : [];
}
